import axios from 'axios';

const API = `${window.location.origin}/api`;

/*
 * External analytics helpers — GA4 + Meta Pixel + Meta Conversions API.
 *
 * Meta setup:
 *   Every fbq event is fired with a unique `eventID` (UUIDv4). The same ID
 *   is POSTed to /api/meta/events which forwards it to Meta Graph API v26.0
 *   server-side. Meta deduplicates browser+server events with matching
 *   {event_name, event_id} within ~48h — critical for iOS 14+/Safari/ad-
 *   blocker recovery. See /app/backend/routes/meta_capi.py.
 *
 *   Purchase uses `purchase-{orderId}` as its event_id so the server-side
 *   Purchase fired from orders.py (see emit_purchase_capi) also dedups.
 */

const safeGtag = (...args) => {
  try { if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag(...args); } catch { /* silent */ }
};
const safeFbq = (...args) => {
  try { if (typeof window !== 'undefined' && typeof window.fbq === 'function') window.fbq(...args); } catch { /* silent */ }
};

// ─── Meta helpers ───────────────────────────────────────────────────────
const readCookie = (name) => {
  try {
    const parts = document.cookie.split('; ');
    const row = parts.find((c) => c.startsWith(`${name}=`));
    return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : undefined;
  } catch { return undefined; }
};

const genEventId = () => {
  try {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fallthrough */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Persistent anonymous visitor id — used as external_id fallback for events
// fired BEFORE the user logs in. Meta accepts any stable per-user id here
// and it dramatically boosts Event Match Quality for cold ad traffic (which
// is 90%+ of first-touch sessions).
//
// CRITICAL: we ALWAYS return a non-empty id — even if localStorage throws
// (private mode / Safari ITP eviction). In that case we fall back to an
// in-memory id for the tab lifetime. Missing external_id is what triggers
// Meta's "Send missing user data parameters" warning and drops EMQ to 3/10.
const ANON_ID_KEY = 'jsf_anon_id';
const _makeAnonId = () => {
  try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (_) {}
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};
const _anonId = (() => {
  try {
    let v = localStorage.getItem(ANON_ID_KEY);
    if (!v) {
      v = _makeAnonId();
      localStorage.setItem(ANON_ID_KEY, v);
    }
    return v;
  } catch {
    // localStorage denied — return an in-memory id so events still have a
    // stable-per-tab external_id instead of nothing.
    return _makeAnonId();
  }
})();

// Meta ClickID (fbc) — the #1 signal Meta wants for ad attribution.
//
// Normally `fbevents.js` reads a `?fbclid=…` URL param and writes the `_fbc`
// cookie. But that's fragile: (a) some browsers block third-party cookies,
// (b) SPA route changes don't re-run fbevents' fbclid capture, (c) if the
// user lands via an in-app browser and switches to Safari mid-session, the
// original cookie is lost.
//
// So we ALSO construct fbc ourselves from the fbclid URL param on any page
// load, cache it in localStorage, and use it as a fallback for every event.
// This is Meta's officially-documented workaround and is what raises the
// "Send Meta ClickID for PageView" EMQ suggestion from 3.0 → 6+/10.
const FBC_KEY = 'jsf_fbc';
const _captureFbcFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    const clickId = url.searchParams.get('fbclid');
    if (!clickId) return;
    // Meta spec: fb.{subdomain_index}.{creation_time_ms}.{fbclid}
    // subdomain_index = 1 for top-level domains (jsfragrances.com).
    const composed = `fb.1.${Date.now()}.${clickId}`;
    localStorage.setItem(FBC_KEY, composed);
  } catch { /* silent */ }
};
_captureFbcFromUrl();

const getFbc = () => {
  const fromCookie = readCookie('_fbc');
  if (fromCookie) return fromCookie;
  try { return localStorage.getItem(FBC_KEY) || undefined; } catch { return undefined; }
};

// Cache the logged-in user identity so every event can add Advanced Matching
// without an extra API round-trip. Set by setMetaUser() from AuthContext.
// Hydrated synchronously from localStorage so events fired BEFORE AuthContext
// resolves (e.g., ViewContent on a direct product-page landing from an ad)
// still carry PII → keeps Meta Event Match Quality (EMQ) high.
const META_USER_KEY = 'jsf_meta_user';
const _hydrate = () => {
  try {
    const raw = localStorage.getItem(META_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
let _metaUser = _hydrate();
export const setMetaUser = (user) => {
  if (!user) {
    _metaUser = null;
    try { localStorage.removeItem(META_USER_KEY); } catch { /* silent */ }
    return;
  }
  _metaUser = {
    email: (user.email || '').trim().toLowerCase() || undefined,
    phone: (user.phone || '').replace(/\D/g, '') || undefined,
    external_id: user.id || user._id || undefined,
  };
  try { localStorage.setItem(META_USER_KEY, JSON.stringify(_metaUser)); } catch { /* silent */ }
};

const forwardToCapi = (eventName, eventId, customData) => {
  // Fire-and-forget — CAPI must NEVER block the browser.
  // Note: we DO mirror PageView server-side even though it's high-frequency,
  // because Meta's EMQ scoring explicitly rewards a server PageView with
  // ClickID (fbc). Missing this was capping our EMQ at ~3/10.
  const body = {
    event_name: eventName,
    event_id: eventId,
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: window.location.href,
    client_user_agent: navigator.userAgent,
    fbp: readCookie('_fbp'),
    fbc: getFbc(),
    email: _metaUser?.email,
    phone: _metaUser?.phone,
    // Fall back to the persistent anonymous id for cold traffic so Meta
    // can still stitch events across pageviews within a session.
    external_id: _metaUser?.external_id || _anonId,
    custom_data: customData || {},
  };
  try {
    // keepalive: true keeps the request alive across page unloads (Purchase
    // often fires just before a redirect to /order-success).
    fetch(`${API}/meta/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
};

/**
 * Fire a Meta standard event on both the browser Pixel and the server-side
 * Conversions API with the same event_id (for dedup). Optionally reuse an
 * existing eventId (e.g., server-generated order id for Purchase).
 */
const fireMeta = (eventName, params = {}, opts = {}) => {
  const eventId = opts.eventId || genEventId();
  safeFbq('track', eventName, params, { eventID: eventId });
  forwardToCapi(eventName, eventId, params);
  return eventId;
};

// ─── GA4 helpers ────────────────────────────────────────────────────────
const productToGA4Item = (productOrId, name, price, qty = 1) => {
  if (productOrId && typeof productOrId === 'object') {
    return {
      item_id: productOrId.id,
      item_name: productOrId.name,
      item_brand: 'JS Fragrance',
      item_category: productOrId.is_signature ? 'JS Originals' : 'Inspired',
      item_category2: productOrId.gender || undefined,
      price: Number(productOrId.base_price || productOrId.price || 0),
      quantity: qty,
    };
  }
  return { item_id: productOrId, item_name: name, item_brand: 'JS Fragrance', price: Number(price || 0), quantity: qty };
};

const isTrackingAllowed = () => {
  try {
    return localStorage.getItem('cookie_consent') !== 'declined';
  } catch {
    return true;
  }
};

// Stable anonymous visitor ID (persists across sessions) + per-tab session ID
const getVisitorId = () => {
  try {
    let v = localStorage.getItem('jsf_visitor_id');
    if (!v) {
      v = (crypto.randomUUID && crypto.randomUUID()) || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('jsf_visitor_id', v);
    }
    return v;
  } catch { return null; }
};
const getSessionId = () => {
  try {
    let s = sessionStorage.getItem('jsf_session_id');
    if (!s) {
      s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('jsf_session_id', s);
    }
    return s;
  } catch { return null; }
};

const track = (type, data = {}) => {
  if (!isTrackingAllowed()) return;
  axios.post(`${API}/analytics/event`, {
    type,
    page: window.location.pathname,
    referrer: document.referrer,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    ...data,
  }).catch(() => {}); // Fire and forget
};

// Lightweight heartbeat ping every 30s while tab is open — powers the "Live now" indicator
let _heartbeatTimer = null;
export const startHeartbeat = () => {
  if (_heartbeatTimer || !isTrackingAllowed()) return;
  const beat = () => track('heartbeat');
  beat();
  _heartbeatTimer = setInterval(beat, 30 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') beat();
  });
};

// ─── Standard events ────────────────────────────────────────────────────
export const trackPageView = () => {
  track('page_view');
  safeGtag('event', 'page_view', { page_path: window.location.pathname });
  // Fire the browser Pixel PageView with a shared event_id so Meta can
  // dedupe against the CAPI-mirrored PageView we're about to POST.
  // This is what unlocks the "server PageView with ClickID" EMQ signal —
  // Meta's #1 recommendation to move EMQ 3.0 → 6+.
  const pvId = genEventId();
  safeFbq('track', 'PageView', {}, { eventID: pvId });
  forwardToCapi('PageView', pvId, {});
};

export const trackProductView = (productId, productName, price = 0) => {
  track('product_view', { product_id: productId, product_name: productName });
  const value = Number(price || 0);
  safeGtag('event', 'view_item', {
    currency: 'PHP', value,
    items: [productToGA4Item(productId, productName, price)],
  });
  fireMeta('ViewContent', {
    content_ids: [productId],
    content_name: productName,
    content_type: 'product',
    contents: [{ id: productId, quantity: 1, item_price: value }],
    value,
    currency: 'PHP',
  });
};

export const trackAddToCart = (productId, productName, price = 0, qty = 1) => {
  track('add_to_cart', { product_id: productId, product_name: productName });
  const unit = Number(price || 0);
  const value = unit * qty;
  safeGtag('event', 'add_to_cart', {
    currency: 'PHP', value,
    items: [productToGA4Item(productId, productName, price, qty)],
  });
  fireMeta('AddToCart', {
    content_ids: [productId],
    content_name: productName,
    content_type: 'product',
    contents: [{ id: productId, quantity: qty, item_price: unit }],
    value,
    currency: 'PHP',
  });
};

export const trackCheckout = (cartItems = [], totalValue = 0) => {
  track('checkout');
  const items = cartItems.map((i) => ({
    id: i.id || i.product_id,
    quantity: i.quantity || 1,
    item_price: Number(i.price || i.base_price || 0),
  }));
  const value = Number(totalValue || 0);
  safeGtag('event', 'begin_checkout', {
    currency: 'PHP', value,
    items: cartItems.map((i) => productToGA4Item(i, i.name, i.price, i.quantity || 1)),
  });
  fireMeta('InitiateCheckout', {
    content_ids: items.map((i) => i.id).filter(Boolean),
    contents: items,
    content_type: 'product',
    num_items: items.reduce((s, i) => s + (i.quantity || 1), 0),
    value,
    currency: 'PHP',
  });
};

export const trackAddPaymentInfo = (paymentMethod, totalValue = 0) => {
  safeGtag('event', 'add_payment_info', {
    currency: 'PHP', value: Number(totalValue || 0), payment_type: paymentMethod,
  });
  fireMeta('AddPaymentInfo', {
    value: Number(totalValue || 0),
    currency: 'PHP',
    content_category: paymentMethod || undefined,
  });
};

export const trackPurchase = (orderId, value, items = [], shippingFee = 0) => {
  track('purchase', { product_id: orderId, value });
  const num = Number(value || 0);
  const contents = items.map((i) => ({
    id: i.id || i.product_id,
    quantity: i.quantity || 1,
    item_price: Number(i.price || i.base_price || 0),
  }));
  safeGtag('event', 'purchase', {
    transaction_id: orderId, currency: 'PHP', value: num,
    shipping: Number(shippingFee || 0),
    items: items.map((i) => productToGA4Item(i, i.name, i.price, i.quantity || 1)),
  });
  // Use a stable event_id derived from orderId so browser + server-side
  // Purchase (from routes/orders.py → emit_purchase_capi) dedupe cleanly.
  fireMeta('Purchase', {
    value: num,
    currency: 'PHP',
    content_ids: contents.map((c) => c.id).filter(Boolean),
    content_type: 'product',
    contents,
    num_items: contents.reduce((s, c) => s + (c.quantity || 1), 0),
    order_id: orderId,
  }, { eventId: `purchase-${orderId}` });
};

// ─── Lead generation events ─────────────────────────────────────────────
export const trackSearch = (query) => {
  if (!query) return;
  safeGtag('event', 'search', { search_term: query });
  fireMeta('Search', { search_string: query });
};
export const trackSignUp = (method = 'email') => {
  safeGtag('event', 'sign_up', { method });
  fireMeta('CompleteRegistration', { content_name: 'Account Signup', status: true });
};
export const trackLogin = (method = 'email') => {
  safeGtag('event', 'login', { method });
};
export const trackLead = (source, label) => {
  safeGtag('event', 'generate_lead', { source, label, currency: 'PHP', value: 0 });
  fireMeta('Lead', { content_name: label || source, content_category: source });
};
export const trackContactSubmit = () => trackLead('contact-form', 'About → Contact');
export const trackQuizComplete = (style) => trackLead('scent-finder', `Quiz: ${style || 'unknown'}`);
export const trackCustomScentSubmit = () => trackLead('custom-scent', 'Custom Scent Builder');
export const trackAmbassadorApply = () => trackLead('ambassador', 'Ambassador Application');
export const trackAddToWishlist = (productId, productName, price = 0) => {
  const value = Number(price || 0);
  safeGtag('event', 'add_to_wishlist', {
    currency: 'PHP', value,
    items: [productToGA4Item(productId, productName, price)],
  });
  fireMeta('AddToWishlist', {
    content_ids: [productId],
    content_type: 'product',
    contents: [{ id: productId, quantity: 1, item_price: value }],
    value,
    currency: 'PHP',
  });
};
export const trackShareProduct = (productId, platform) => {
  safeGtag('event', 'share', { method: platform, content_type: 'product', item_id: productId });
};
